import { Suspense } from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter, HashRouter } from 'react-router-dom'
import { ConfigProvider } from 'antd'

import { E_Colors } from '@/helpers/colors'
import { AppRouter } from '@/router'
import { AppLayout } from '@/b-components/app-layout'
import { PWAInstallPrompt } from '@/b-components/pwa/pwa-install-prompt'
import { PWAStatus } from '@/b-components/pwa/pwa-status'
import { registerServiceWorker } from '@/b-components/pwa/helpers'

import { Spin } from '@/components'
import { YandexMetrikaTracker } from '@/b-components'
import { AppGlobalStyled } from '@/app-global.styles'
import { envs } from './envs'
import { profile } from '@/profile'
import { isCordovaApp } from '@/helpers'
import { routes } from '@/router/routes'


registerServiceWorker()

const CORDOVA_TOPUP_PATH_RE = /(?:^|\/)(?:office\/)?topup\/([^/?#]+)\/?$/i

const extractTopupRouteFromCordovaUrl = (rawUrl: string): string | null => {
  const normalizedRawUrl = String(rawUrl || '').trim()
  if (!normalizedRawUrl) return null

  const resolveRouteFromPath = (pathname: string) => {
    const pathMatch = pathname.match(CORDOVA_TOPUP_PATH_RE)
    if (!pathMatch?.[1]) return null
    return `${routes.topupReturn(pathMatch[1])}?from_cordova=1`
  }

  try {
    const parsedUrl = new URL(normalizedRawUrl)

    // Ex: eraapp://office/topup/123?...
    const hostAndPath = `/${parsedUrl.host}${parsedUrl.pathname}`
    const routeFromHostAndPath = resolveRouteFromPath(hostAndPath)
    if (routeFromHostAndPath) return routeFromHostAndPath

    // Ex: https://.../office/topup/123?...
    const routeFromPathname = resolveRouteFromPath(parsedUrl.pathname)
    if (routeFromPathname) return routeFromPathname

    if (parsedUrl.searchParams.get('from_cordova') === '1') {
      const requestId = parsedUrl.searchParams.get('request_id') || parsedUrl.searchParams.get('id')
      if (requestId) {
        return `${routes.topupReturn(requestId)}?from_cordova=1`
      }
    }
  } catch {
    // ignore malformed deep-link payload
  }

  return null
}

const navigateByCordovaDeepLink = (rawUrl: string) => {
  if (typeof window === 'undefined') return

  const targetRoute = extractTopupRouteFromCordovaUrl(rawUrl)
  if (!targetRoute) return

  window.location.hash = targetRoute.startsWith('/') ? `#${targetRoute}` : `#/${targetRoute}`
}

if (isCordovaApp && typeof window !== 'undefined') {
  // cordova-plugin-customurlscheme invokes this global function when app is opened by deep-link.
  window.handleOpenURL = (rawUrl: string) => {
    window.setTimeout(() => {
      navigateByCordovaDeepLink(rawUrl)
    }, 0)
  }
}

const Router = isCordovaApp ? HashRouter : BrowserRouter

ReactDOM.createRoot(
  document.getElementById('root')!
).render(
  <>
    <AppGlobalStyled />

    <ConfigProvider theme={{
      token: {
        colorTextBase: E_Colors.TextPrimary,
      }
    }}>
      <Suspense fallback={<Spin size='large' />}>
        <Router>
          {profile.isYandexMetric && <YandexMetrikaTracker counterId={envs.YANDEX_METRIKA_COUNTER_ID} />}

          <AppLayout>
            <AppRouter />
            <PWAInstallPrompt />
            <PWAStatus />
          </AppLayout>
        </Router>
      </Suspense>
    </ConfigProvider>
  </>
)
